// Area: nav (owner platform-contracts in wave 1, shell-nav in wave 2). The rails: apps and objects,
// pinned items, recent items, badges and the member's rail state (collapsed, folded groups).
import { z } from 'zod';
import {
  NavDTO,
  NavItem,
  NavItemKind,
  NavItemRef,
  RailState,
  RailStateInput,
  RecentItem,
} from '@dataroom/core';
import { defineContract } from '../define';
import { q } from '../query';

export { NavDTO, NavItem, NavItemKind, NavItemRef, RailState, RailStateInput, RecentItem };

export const navContract = defineContract({
  method: 'GET',
  path: '/nav',
  response: NavDTO,
  summary: 'Apps with objects, pinned items, badges, rail state and instance info for the navigation rails',
});

// -- Rail state ----------------------------------------------------------------------------------------

export const updateRailStateContract = defineContract({
  method: 'PATCH',
  path: '/nav/rail',
  body: RailStateInput,
  response: RailState,
  summary: "Remember the caller's rail state (collapsed with ⌘\\ or automatic, folded app groups)",
});

// -- Pinned items --------------------------------------------------------------------------------------

export const pinItemContract = defineContract({
  method: 'POST',
  path: '/nav/pins',
  body: NavItemRef,
  response: z.object({ pinned: z.array(NavItem) }),
  summary: 'Pin a record, page, deck, document template, dashboard or view to the rail (idempotent)',
});

export const unpinItemContract = defineContract({
  method: 'DELETE',
  path: '/nav/pins/:kind/:id',
  params: z.object({ kind: NavItemKind, id: z.uuid() }),
  response: z.void(),
  summary: 'Unpin an item from the rail (views: also leaves the view favourites)',
});

export const reorderPinsContract = defineContract({
  method: 'PUT',
  path: '/nav/pins/order',
  body: z
    .object({
      /** Item keys `<kind>:<id>` in the new order (every pinned item, as the rail shows them). */
      keys: z.array(z.string().regex(/^[a-z]+:[0-9a-f-]{36}$/)).max(200),
    })
    .strict(),
  response: z.object({ pinned: z.array(NavItem) }),
  summary: 'Reorder the pinned items of the rail',
});

// -- Recent items --------------------------------------------------------------------------------------

export const recentItemsContract = defineContract({
  method: 'GET',
  path: '/nav/recent',
  query: z.object({ limit: q.int().min(1).max(20).default(5) }),
  response: z.object({ items: z.array(RecentItem) }),
  summary: 'What the caller opened last (unreadable and deleted items left out)',
});

export const trackRecentContract = defineContract({
  method: 'POST',
  path: '/nav/recent',
  body: NavItemRef,
  response: z.void(),
  summary: 'Remember that the caller opened an item (the web shell calls it on route open)',
});

export * from './setup';
